import { useState } from "react";
import { Box, Chip, Popover, Stack, Tooltip, Typography } from "@mui/material";
import type { AuthorityByType, DataSource, PerTypeAuthorityInfo } from "@/types";
import { formatLongDate, formatDate, fromISO, DATE_FORMATS } from "@/utils/formatters/dateFormatter";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

interface ClaimsSourceChipProps {
  /** Single-source authority (bundled policy). Omit when `perType` is provided. */
  source?: DataSource;
  /** Compact mode shows "R" / "C" for list views */
  compact?: boolean;
  /** ISO datetime — appended to tooltip when present */
  lastUpdated?: string;
  /**
   * ISO date the value is current through (the reviewer-confirmed paid-through
   * / as-of date, ADR-051). When present, shown as "As of …" in place of the
   * last-updated line — it's the meaningful date for a carrier-reported value.
   */
  asOf?: string;
  /** e.g. carrier or TPA name — appended to tooltip when present */
  sourceName?: string;
  /**
   * Per-claim-type authority for a carve-out claimant (ADR-077). When present,
   * the chip renders a single "Blended" affordance whose popover shows the
   * medical and Rx sources + per-type as-of independently, instead of one
   * source. Takes precedence over `source`.
   */
  perType?: AuthorityByType;
  /**
   * Include the per-type dollar figures in the Blended tooltip. Off by default:
   * the amounts reconcile to **Total Paid** and to nothing else, and this chip
   * is mounted as the adornment on Spend Over Deductible too (and in the family
   * layout, on that card only). Showing them there would assert a split of a
   * number they do not sum to.
   */
  showAmounts?: boolean;
}

const config = {
  reported: { label: "Reported", compact: "R", tooltip: "Reported by carrier" },
  calculated: { label: "Calculated", compact: "C", tooltip: "Calculated from TPA claim items" },
} as const;

/**
 * Config lookup that tolerates an out-of-band authority value. `DataSource` is
 * typed "calculated" | "reported", but the BE `buildAuthorityByType` defaults
 * `Authority` to "" when a per-type column is nil, and those columns are
 * nullable — an unguarded `config[""]` would throw at render.
 *
 * Not reachable today: the per-type resolver always writes both authorities
 * whenever `authority_by_type` is attached. Guarded anyway so a future
 * one-sided write degrades to a wrong-ish label instead of a blank card
 * (PR !4428 review).
 */
const cfgFor = (authority: DataSource) => config[authority] ?? config.calculated;

const chipSx = (compact: boolean) => ({
  height: 20,
  fontSize: "0.65rem",
  fontWeight: 600,
  color: "text.secondary",
  borderColor: "divider",
  "& .MuiChip-label": {
    px: compact ? 0.5 : 0.75,
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
  },
});

/**
 * True when an ISO date string is a real, displayable date — guards against an
 * unset backend `time.Time` that serializes as the zero value (`0001-01-01`)
 * and would otherwise render as "Dec 31, 0001".
 */
const isRealDate = (iso?: string): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  return !isNaN(d.getTime()) && d.getFullYear() > 1;
};

/** Builds the full tooltip, preferring an "As of" date over "Last updated". */
const buildTooltip = (base: string, sourceName?: string, lastUpdated?: string, asOf?: string): string => {
  const parts = [sourceName ? `${base} (${sourceName})` : base];
  if (isRealDate(asOf)) {
    // The as-of is a date-only value; format from its YYYY-MM-DD part so a
    // midnight-UTC datetime doesn't shift a day in negative-offset timezones.
    parts.push(`As of: ${formatDate(fromISO(asOf!), DATE_FORMATS.DISPLAY_LONG)}`);
  } else if (isRealDate(lastUpdated)) {
    parts.push(`Last updated: ${formatLongDate(lastUpdated!)}`);
  }
  return parts.join("\n");
};

/**
 * Hover content for the "Blended" chip: the actual medical/Rx split that makes
 * up the total, not just a prompt to click. On a carve-out the headline total is
 * medical + Rx resolved independently, and the split is the first thing a
 * reviewer wants — surfacing it on hover saves a click and makes a wrong number
 * obvious at a glance. The popover still carries source names and per-type
 * as-of dates.
 */
const BlendedTooltip = ({ perType, showAmounts }: { perType: AuthorityByType; showAmounts: boolean }) => (
  <Box>
    <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
      Medical {showAmounts ? `${formatUSD(perType.medical.total_paid)} · ` : ""}
      {cfgFor(perType.medical.authority).label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
      Rx {showAmounts ? `${formatUSD(perType.rx.total_paid)} · ` : ""}
      {cfgFor(perType.rx.authority).label}
    </Typography>
    <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.8 }}>
      Click for sources and as-of dates
    </Typography>
  </Box>
);

/** Accessible name for the Blended chip — mirrors the tooltip content. */
const blendedAriaLabel = (perType: AuthorityByType, showAmounts: boolean) => {
  const part = (label: string, info: AuthorityByType["medical"]) =>
    `${label} ${showAmounts ? `${formatUSD(info.total_paid)} ` : ""}${cfgFor(info.authority).label}`;
  return `Blended claims authority — ${part("medical", perType.medical)}, ${part("Rx", perType.rx)}`;
};

/** One "Medical" / "Rx" row in the per-type (carve-out) popover. */
const PerTypeRow = ({ label, info }: { label: string; info: PerTypeAuthorityInfo }) => {
  const cfg = cfgFor(info.authority);
  const asOf = isRealDate(info.as_of) ? ` · as of ${formatDate(fromISO(info.as_of!), DATE_FORMATS.DISPLAY_LONG)}` : "";
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {cfg.label}
        {info.source_name ? ` (${info.source_name})` : ""}
        {asOf}
      </Typography>
    </Stack>
  );
};

/**
 * Subtle chip indicating whether claims data comes from carrier-reported YTD
 * totals or system-calculated aggregates from claim line items. For a carve-out
 * claimant (`perType`), it shows a single "Blended" chip whose popover breaks
 * down medical vs Rx authority independently (ADR-077 §5).
 */
export const ClaimsSourceChip = ({
  source,
  compact = false,
  lastUpdated,
  asOf,
  sourceName,
  perType,
  showAmounts = false,
}: ClaimsSourceChipProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Per-type (carve-out): one "Blended" chip → popover with medical/Rx split.
  if (perType) {
    const label = compact
      ? `${cfgFor(perType.medical.authority).compact}/${cfgFor(perType.rx.authority).compact}`
      : "Blended";
    return (
      <>
        {/*
          describeChild is required, not cosmetic. With a non-string title MUI
          sets aria-labelledby on the child while the tooltip is open; the Chip
          is focusable and focus opens the tooltip, so that would win the
          accessible name and drop the visible word "Blended" from it —
          WCAG 2.5.3 Label in Name (A), and it would silently defeat the
          aria-label below. describeChild makes the tooltip the DESCRIPTION and
          leaves the chip's own label as its name.
        */}
        <Tooltip title={<BlendedTooltip perType={perType} showAmounts={showAmounts} />} arrow describeChild>
          <Chip
            label={label}
            size="small"
            variant="outlined"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label={blendedAriaLabel(perType, showAmounts)}
            aria-haspopup="dialog"
            sx={{ ...chipSx(compact), cursor: "pointer" }}
          />
        </Tooltip>
        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 1.5, minWidth: 240 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
              Blended authority (Rx carve-out)
            </Typography>
            <PerTypeRow label="Medical" info={perType.medical} />
            <PerTypeRow label="Rx" info={perType.rx} />
          </Box>
        </Popover>
      </>
    );
  }

  if (!source) return null;

  const { label, compact: compactLabel, tooltip: baseTooltip } = config[source];
  const tooltip = buildTooltip(baseTooltip, sourceName, lastUpdated, asOf);

  return (
    <Tooltip title={tooltip} arrow>
      <Chip label={compact ? compactLabel : label} size="small" variant="outlined" sx={chipSx(compact)} />
    </Tooltip>
  );
};
